import { useQuery } from '@tanstack/react-query';

import { fetchFeatureFlagAssignments } from '../../apis/merchant-admin-server/api';
import { EligibilityCriteria } from '../../apis/merchant-admin-server/types';

type Options = {
  enabled: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
};

const DEFAULT_OPTIONS = {
  enabled: true,
};

/**
 * Get a hold of generic Regatta feature flags.
 *
 * For more info see https://regatta.wolt.com/redoc
 *
 * Example usage:
 *
 * ```tsx
 * const { data: featureFlags } = useFeatureFlags(['use_address_picker_v2'], {"venue_country": venue.country});
 *
 * if (featureFlags?.use_address_picker_v2) {
 *   return <AddressPickerV2 />;
 * }
 * ```
 *
 * @param featureFlags The feature flag(s) to check
 * @param eligibilityCriteria The feature flag(s) criteria to check
 * @param options Extra options, e.g. whether or not the flag is enabled
 * @returns The feature flag(s) or `null` as `data`, error as `error`, and loading state as `loading`
 */
const useFeatureFlags = <T extends string>(
  featureFlags: T[] = [],
  eligibilityCriteria: EligibilityCriteria,
  options: Options = DEFAULT_OPTIONS,
) => {
  return useQuery<Record<T, string | null>>(
    ['/regatta/generic/feature_flag/assignments', featureFlags, eligibilityCriteria],
    () => fetchFeatureFlagAssignments(featureFlags, eligibilityCriteria),
    options,
  );
};

export default useFeatureFlags;
